import Box from '@mui/material/Box';
import React from 'react';

import { EquipmentFaultPanelProps } from './EquipmentFaultPanel.props';
import { ActionPermissions } from '../../../../../@types';
import { FieldGrid, FieldItem } from '../../../../../components/atoms/FieldGrid';
import { Panel } from '../../../../../components/atoms/Panel';
import { PermissionLink } from '../../../../../components/atoms/PermissionLink';
import { EditableSelect } from '../../../../../components/molecules/EditableSelect';
import { EditableSerialSearch } from '../../../../../components/molecules/EditableSerialSearch';
import { EditableText } from '../../../../../components/molecules/EditableText';
import { sanitizeElement } from '../../../../../utils/common';

const SafeHtml: React.FC<{ html: string }> = ({ html }) => {
  if (!html || html === '—') return <>{html}</>;
  if (/<[a-z][\s\S]*>/i.test(html)) {
    return (
      <Box
        component="span"
        sx={{ '& p': { m: 0 }, fontSize: 'inherit' }}
        dangerouslySetInnerHTML={{ __html: sanitizeElement(html) }}
      />
    );
  }
  return (
    <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>
      {html}
    </Box>
  );
};

export const EquipmentFaultPanel: React.FC<EquipmentFaultPanelProps> = ({
  service,
  canEditReferences = false,
  canEditReportedInfo = false,
  canEditReportedSerial = false,
  reportedModelOptions = [],
  reportedFaultOptions = [],
  onReportedInfoUpdate,
  onSerialPick,
}) => {
  const creationNotes = service?.creationNotes;

  const saveReference =
    (field: 'referenceNo1' | 'referenceNo2' | 'referenceNo3') => (val: string) =>
      onReportedInfoUpdate?.({ [field]: val }, { [field]: val });

  const saveFault = (val: string) => {
    const numVal = val ? Number(val) : undefined;
    const label = reportedFaultOptions.find((o) => o.value === val)?.label ?? '';
    onReportedInfoUpdate?.(
      { reportedFaultAndDescriptionId: numVal },
      {
        reportedFaultAndDescriptionId: numVal,
        reportedFaultAndDescription: label,
      }
    );
  };

  const saveSerial = ({ serial, partId }: { serial: string; partId: number }) => {
    // Prefer the auto-derive handler (resolves the matching Model from the
    // chosen part's type). Fall back to a serial-only update when callers
    // haven't wired it up.
    if (onSerialPick) {
      void onSerialPick({ serial, partId });
      return;
    }
    onReportedInfoUpdate?.(
      { reportedSerialNumber: partId },
      { reportedSerialNumber: serial, reportedSerialNumberPartId: partId }
    );
  };

  const serialDisplay =
    service?.reportedSerialNumber && service?.reportedSerialNumberPartId ? (
      <PermissionLink
        permission={ActionPermissions.Part_Search}
        href={`/parts/${service.reportedSerialNumberPartId}`}
      >
        {service.reportedSerialNumber}
      </PermissionLink>
    ) : (
      service?.reportedSerialNumber || undefined
    );

  return (
    <Panel title="Reported Information">
      <FieldGrid columns={2}>
        <FieldItem
          label="Reference #1"
          value={
            <EditableText
              value={service?.referenceNo1 ?? ''}
              isEditable={canEditReferences && !!onReportedInfoUpdate}
              ariaLabel="Reference #1"
              onSave={saveReference('referenceNo1')}
            />
          }
        />
        <FieldItem
          label="Serial"
          value={
            <EditableSerialSearch
              value={service?.reportedSerialNumber ?? ''}
              displayValue={serialDisplay}
              isEditable={canEditReportedSerial && (!!onSerialPick || !!onReportedInfoUpdate)}
              ariaLabel="Serial"
              onSave={saveSerial}
            />
          }
        />
        <FieldItem
          label="Reference #2"
          value={
            <EditableText
              value={service?.referenceNo2 ?? ''}
              isEditable={canEditReferences && !!onReportedInfoUpdate}
              ariaLabel="Reference #2"
              onSave={saveReference('referenceNo2')}
            />
          }
        />
        <FieldItem
          label="Model"
          value={
            <EditableSelect
              value={service?.reportedModelId ? String(service.reportedModelId) : ''}
              displayValue={service?.reportedModel || undefined}
              isEditable={false}
              ariaLabel="Model"
              options={reportedModelOptions}
              placeholder="Select model"
              onSave={() => undefined}
            />
          }
        />
        <FieldItem
          label="Reference #3"
          value={
            <EditableText
              value={service?.referenceNo3 ?? ''}
              isEditable={canEditReferences && !!onReportedInfoUpdate}
              ariaLabel="Reference #3"
              onSave={saveReference('referenceNo3')}
            />
          }
        />
        <FieldItem
          label="Fault Description"
          value={
            <EditableSelect
              value={
                service?.reportedFaultAndDescriptionId
                  ? String(service.reportedFaultAndDescriptionId)
                  : ''
              }
              displayValue={service?.reportedFaultAndDescription || undefined}
              isEditable={canEditReportedInfo && !!onReportedInfoUpdate}
              ariaLabel="Fault Description"
              options={reportedFaultOptions}
              placeholder="Select fault"
              onSave={saveFault}
            />
          }
        />
        <FieldItem
          label="Reported Notes"
          value={
            creationNotes ? (
              <SafeHtml html={creationNotes} />
            ) : (
              <Box component="span" sx={{ color: 'text.disabled' }}>
                N/A
              </Box>
            )
          }
          fullWidth
        />
      </FieldGrid>
    </Panel>
  );
};
